import { graphOf } from '../map';
import {
  controlsRegion,
  territoryCount,
  getScores,
  neighborsOf,
  ownedTerritories,
  regionOf,
  territoriesOfRegion,
} from '../selectors';
import type { PlayerId, PlayerView, TerritoryId } from '../types';

/** Soma das tropas inimigas vizinhas. */
export function threat(v: PlayerView, t: TerritoryId): number {
  const owner = v.territories[t].owner;
  let n = 0;
  for (const nb of neighborsOf(v, t)) if (v.territories[nb].owner !== owner) n += v.territories[nb].troops;
  return n;
}

export function isBorder(v: PlayerView, t: TerritoryId): boolean {
  const owner = v.territories[t].owner;
  return neighborsOf(v, t).some((nb) => v.territories[nb].owner !== owner);
}

/** Quantos territórios da região faltam para `p` completá-la. */
export function missingInRegion(v: PlayerView, p: PlayerId, region: string): number {
  return territoriesOfRegion(v, region).filter((t) => v.territories[t].owner !== p).length;
}

/** Distância (em passos por territórios próprios) até a fronteira mais próxima. */
export function borderDistance(v: PlayerView, p: PlayerId): Map<TerritoryId, number> {
  const dist = new Map<TerritoryId, number>();
  const queue: TerritoryId[] = [];
  for (const t of ownedTerritories(v, p)) {
    if (isBorder(v, t)) {
      dist.set(t, 0);
      queue.push(t);
    }
  }
  while (queue.length) {
    const cur = queue.shift()!;
    for (const nb of neighborsOf(v, cur)) {
      if (v.territories[nb].owner !== p || dist.has(nb)) continue;
      dist.set(nb, dist.get(cur)! + 1);
      queue.push(nb);
    }
  }
  return dist;
}

/** Valor estratégico de reforçar `t`: ameaça + proximidade de completar a região. */
export function reinforceScore(v: PlayerView, p: PlayerId, t: TerritoryId): number {
  const own = v.territories[t].troops;
  const danger = threat(v, t);
  if (danger === 0) return -10 - own;
  let score = danger - own * 0.6;
  const region = regionOf(v, t);
  const missing = missingInRegion(v, p, region);
  if (missing === 0) score += 4; // defender região completa
  else if (missing <= 2) score += 6 - missing * 2;
  // vizinho inimigo fraco: bom ponto de ataque
  const weakest = Math.min(
    ...neighborsOf(v, t)
      .filter((nb) => v.territories[nb].owner !== p)
      .map((nb) => v.territories[nb].troops),
  );
  if (weakest <= 2) score += 2;
  return score;
}

/** O jogador com maior pontuação (exceto `exclude`). */
export function leader(v: PlayerView, exclude: PlayerId | null): PlayerId {
  return getScores(v)
    .filter((l) => l.player !== exclude && v.players[l.player].alive)
    .sort((a, b) => b.score - a.score || b.troops - a.troops)[0].player;
}

/** Regiões que a missão de `p` quer completas (inclui a região "extra" mais próxima no plusAny). */
export function missionRegions(v: PlayerView, p: PlayerId): string[] {
  const m = v.players[p].mission;
  if (!m || m.type !== 'regions') return [];
  const out = [...m.regions];
  if (m.plusAny) {
    const others = v.info.content.map.regions
      .map((r) => r.id)
      .filter((r) => !m.regions.includes(r))
      .sort((a, b) => missingInRegion(v, p, a) - missingInRegion(v, p, b));
    out.push(...others.slice(0, m.plusAny));
  }
  return out;
}

/** Quanto conquistar (ou manter) `t` avança a missão secreta de `p`. */
export function missionWeight(v: PlayerView, p: PlayerId, t: TerritoryId): number {
  const m = v.players[p].mission;
  if (!m) return 0;
  switch (m.type) {
    case 'regions':
      return missionRegions(v, p).includes(regionOf(v, t)) ? 3 : 0;
    case 'territories':
      return 1;
    case 'ports':
      return graphOf(v.info.content.map).ports.has(t) ? 2.5 : 0;
    case 'eliminate': {
      const owner = v.territories[t].owner;
      return v.players[owner]?.color === m.color && owner !== p ? 3 + (territoryCount(v, owner) <= 2 ? 3 : 0) : 0;
    }
    case 'bossKills':
      return 0;
  }
}

/** Bônus de ataque: completar região, quebrar região inimiga, avançar a missão. */
export function attackBonus(v: PlayerView, p: PlayerId, to: TerritoryId): number {
  const region = regionOf(v, to);
  const defender = v.territories[to].owner;
  let bonus = 0;
  const missing = missingInRegion(v, p, region);
  if (missing === 1) bonus += 3;
  else if (missing === 2) bonus += 1;
  if (controlsRegion(v, defender, region)) bonus += 2;
  // Regras de era: cabeça a prêmio rende carta; quem tem caveira perde carta e loot.
  if (v.info.ruleset.bounty && v.bounty === defender && !v.turn.bountyClaimed) bonus += 1.5;
  if (v.skulls.includes(defender)) bonus += 1;
  return bonus + missionWeight(v, p, to);
}

/** Valor de um território próprio (para defender): região completa, missão, tamanho. */
export function holdValue(v: PlayerView, p: PlayerId, t: TerritoryId): number {
  let value = v.territories[t].troops * 0.3 + missionWeight(v, p, t);
  if (controlsRegion(v, p, regionOf(v, t))) value += 3;
  return value;
}

/** Tropas mínimas por território que a missão pede (Ocupação); 1 se não pede. */
export function occupationTroops(v: PlayerView): number {
  const m = v.players[v.me!].mission;
  return m?.type === 'territories' && m.minTroops ? m.minTroops : 1;
}

/**
 * Alvo para a Passagem do Capitão: porto inimigo fora de alcance normal, atacável a partir
 * de um porto próprio com proporção de pelo menos 1,5 para 1, que completa uma região,
 * avança a missão ou quebra uma região inimiga.
 */
export function captainTarget(v: PlayerView): { from: TerritoryId; to: TerritoryId } | null {
  const me = v.me!;
  const ports = graphOf(v.info.content.map).ports;
  let best: { from: TerritoryId; to: TerritoryId; score: number } | null = null;
  for (const from of ports) {
    if (v.territories[from].owner !== me || v.territories[from].troops < 3) continue;
    if (v.effects.some((e) => e.territory === from && (e.kind === 'frozen' || e.kind === 'paralyze'))) continue;
    for (const to of ports) {
      const def = v.territories[to];
      if (def.owner === me || neighborsOf(v, from).includes(to)) continue;
      const ratio = v.territories[from].troops / def.troops;
      if (ratio < 1.5) continue;
      const region = regionOf(v, to);
      const completes = missingInRegion(v, me, region) === 1;
      const breaks = controlsRegion(v, def.owner, region);
      const mission = missionWeight(v, me, to) > 1;
      if (!completes && !breaks && !mission) continue;
      const score = ratio + attackBonus(v, me, to);
      if (!best || score > best.score) best = { from, to, score };
    }
  }
  return best;
}
